import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Trash2, Upload, X } from 'lucide-react';

let ReactQuill;
try {
  ReactQuill = require('react-quill');
  require('react-quill/dist/quill.snow.css');
} catch (e) {
  console.warn('React Quill not available, using textarea fallback');
}

const AdminBlogManage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  const [formData, setFormData] = useState({
    title_tc: '',
    title_en: '',
    content_tc: '',
    content_en: '',
    image_url: '',
    published: true
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/admin/login');
      return;
    }
    fetchPosts();
  }, [user, navigate]);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts`);
      setPosts(response.data.posts);
    } catch (error) {
      toast.error('無法載入文章');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };
      
      // Upload image if there's a new one
      let imageUrl = formData.image_url;
      if (imageFile) {
        toast.info('正在上傳圖片...');
        imageUrl = await handleImageUpload();
      }
      
      const submitData = {
        ...formData,
        image_url: imageUrl,
        date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, ' ')
      };

      if (currentPost) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts/${currentPost.post_id}`, submitData, config);
        toast.success('文章更新成功');
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts`, submitData, config);
        toast.success('文章建立成功');
      }
      resetForm();
      fetchPosts();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || '操作失敗');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('確定要刪除嗎？')) return;
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts/${postId}`, config);
      toast.success('刪除成功');
      fetchPosts();
    } catch (error) {
      toast.error('刪除失敗');
    }
  };

  const handleEdit = (post) => {
    setCurrentPost(post);
    setFormData({
      title_tc: post.title_tc,
      title_en: post.title_en,
      content_tc: post.content_tc,
      content_en: post.content_en,
      image_url: post.image_url || '',
      published: post.published
    });
    setIsEditing(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async () => {
    if (!imageFile) return formData.image_url;
    
    try {
      const formDataImg = new FormData();
      formDataImg.append('image', imageFile);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/upload/image`,
        formDataImg,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      return response.data.url;
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('圖片上傳失敗');
      return formData.image_url;
    }
  };

  const resetForm = () => {
    setCurrentPost(null);
    setFormData({
      title_tc: '',
      title_en: '',
      content_tc: '',
      content_en: '',
      image_url: '',
      published: true
    });
    setImageFile(null);
    setImagePreview('');
    setIsEditing(false);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="admin-blog-manage">
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-2xl font-bold">Blog 管理</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {!isEditing ? (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold">文章列表</h2>
              <Button onClick={() => setIsEditing(true)} className="rounded-full" data-testid="add-post-btn">
                <Plus className="w-4 h-4 mr-2" />
                新增文章
              </Button>
            </div>

            <div className="grid gap-4">
              {posts.map((post) => (
                <div key={post.post_id} className="bg-white p-6 rounded-2xl shadow-sm" data-testid={`post-item-${post.post_id}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{post.title_tc}</h3>
                      <p className="text-sm text-slate-500">{new Date(post.created_at).toLocaleDateString('zh-HK')}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(post)} data-testid={`edit-btn-${post.post_id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(post.post_id)} data-testid={`delete-btn-${post.post_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white p-8 rounded-3xl">
            <h2 className="text-3xl font-bold mb-8">{currentPost ? '編輯文章' : '新增文章'}</h2>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="blog-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">中文標題</label>
                  <Input
                    value={formData.title_tc}
                    onChange={(e) => setFormData({...formData, title_tc: e.target.value})}
                    required
                    data-testid="title-tc-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">英文標題</label>
                  <Input
                    value={formData.title_en}
                    onChange={(e) => setFormData({...formData, title_en: e.target.value})}
                    required
                    data-testid="title-en-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">文章圖片</label>
                <div className="space-y-4">
                  <div className="flex gap-4 items-start">
                    <div className="flex-1">
                      <label className="cursor-pointer">
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 hover:border-primary transition-colors">
                          <div className="flex flex-col items-center gap-2">
                            <Upload className="w-8 h-8 text-slate-400" />
                            <span className="text-sm text-slate-600">點擊上傳圖片</span>
                            <span className="text-xs text-slate-400">或拖放圖片到此處</span>
                          </div>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                          data-testid="image-upload-input"
                        />
                      </label>
                    </div>
                    {(imagePreview || formData.image_url) && (
                      <div className="relative w-32 h-32">
                        <img
                          src={imagePreview || formData.image_url}
                          alt="Preview"
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImageFile(null);
                            setImagePreview('');
                            setFormData({...formData, image_url: ''});
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="text-sm text-slate-500">
                    或輸入圖片URL:
                    <Input
                      value={formData.image_url}
                      onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                      placeholder="https://..."
                      className="mt-2"
                      data-testid="image-url-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">中文內容</label>
                {ReactQuill ? (
                  <div className="quill-editor">
                    <ReactQuill 
                      value={formData.content_tc}
                      onChange={(value) => setFormData({...formData, content_tc: value})}
                      theme="snow"
                    />
                  </div>
                ) : (
                  <Textarea
                    value={formData.content_tc}
                    onChange={(e) => setFormData({...formData, content_tc: e.target.value})}
                    rows={10}
                    className="font-mono"
                    placeholder="輸入中文內容（支持HTML）"
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">英文內容</label>
                {ReactQuill ? (
                  <div className="quill-editor">
                    <ReactQuill 
                      value={formData.content_en}
                      onChange={(value) => setFormData({...formData, content_en: value})}
                      theme="snow"
                    />
                  </div>
                ) : (
                  <Textarea
                    value={formData.content_en}
                    onChange={(e) => setFormData({...formData, content_en: e.target.value})}
                    rows={10}
                    className="font-mono"
                    placeholder="Enter English content (HTML supported)"
                  />
                )}
              </div>

              <div className="flex gap-4">
                <Button type="submit" className="rounded-full" data-testid="submit-btn">
                  {currentPost ? '更新' : '建立'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm} className="rounded-full" data-testid="cancel-btn">
                  取消
                </Button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminBlogManage;